// MonthEndJournalPage — 月末仕訳ページ (2026-06-13, hash route #month_end)
//
// 定額 / 在庫 / 按分 / ヘルプ / 前払 / 社員割引 / 原価率 の約22 kind を freee 振替伝票
// (manual_journals) で計上する。生成→確認→承認→登録 chain。
//   - 「生成」= POST /api/month-end/generate (全 kind 再生成)
//   - 一覧 = GET /api/month-end/journals?year_month=YYYY-MM
//   - ドリルダウン = 振替伝票明細を借方/貸方に分割表示 + source_meta 折りたたみ
//   - 承認・登録 = approve(dry_run:true) → 確認ダイアログ(payload+Σ検算) → openSession →
//                 approve(dry_run:false) を **UIクリック起点で直接 fetch** (R2準拠)
//   - 却下 = reject
//   - 設定パネル = setting_key 別の手入力 UI (section別表 + raw JSON fallback)
//
// 設計参照: month_end_journal_design.md / month_end_decisions.md
// 雛形: FixedJournalVerificationPage(承認テーブル) / BudgetActualPage(会計期/月) /
//       SeibuCashLedgerPage(月切替) / BankProcessingPage(承認chain dialog)
// 注: dry_run:false の実 freee 登録は user の承認 click 起点のみ。Claude 自発呼出禁止。

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  PageTitle,
  SectionTitle,
  SubSectionTitle,
  CardBase,
  ColumnBase,
  HStack,
  VStack,
  Button,
  SelectBox,
  Text,
  Note,
  StatusIcon,
  Breadcrumbs,
  MessageBlock,
  FloatingMessageBlock,
  InlineSpinner,
  TextField,
  TextArea,
  TabBar,
} from '@freee_jp/vibes';
import {
  fetchMonthEndJournals,
  fetchMonthEndJournal,
  generateMonthEndJournals,
  approveMonthEndJournalDryRun,
  approveMonthEndJournal,
  rejectMonthEndJournal,
  bulkApproveMonthEndDryRun,
  bulkApproveMonthEnd,
  MonthEndBulkDryRun,
  MonthEndBulkResult,
  fetchMonthEndSettings,
  saveMonthEndSetting,
  openSessionForMonthEnd,
  fetchSectionsMap,
  fetchAccountItemsMap,
  fetchItemsMap,
  fetchPartnersMap,
  MonthEndJournal,
  MonthEndJournalLine,
  MonthEndJournalStatus,
  MonthEndApproveDryRun,
  API_BASE,
} from '../api/client';
import { UNIFIED_COLOR } from '../lib/statusVocab';

// ---------------------------------------------------------------------------
// 会計期 / 月度 (BudgetActualPage 踏襲) — 80期=2025-10〜2026-09
// ---------------------------------------------------------------------------
// 78期=23/10-24/9, 79期=24/10-25/9, 80期(当期)=25/10-26/9
const TERMS = [80, 79, 78];
// 月度ラベル (index 0 = 10月 … 11 = 9月)
const FISCAL_MONTH_LABEL = ['10月', '11月', '12月', '1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月'];

// 会計期の開始 calendar year (10月始まり)。term=80 → 2025 (期首2025-10)。
function termStartYear(term: number): number {
  // 80期期首 = 2025-10 → startYear=2025。term-1 ごとに -1 年。
  return 2025 - (80 - term);
}

// 会計期 term + 月度index(0=10月) → 'YYYY-MM'
function fiscalToYearMonth(term: number, monthIdx: number): string {
  const startY = termStartYear(term);
  // monthIdx 0..2 → 10/11/12月 (= startY), 3..11 → 1〜9月 (= startY+1)
  const calMonth = ((9 + monthIdx) % 12) + 1; // idx0 → 10, idx3 → 1, idx11 → 9
  const calYear = monthIdx <= 2 ? startY : startY + 1;
  return `${calYear}-${String(calMonth).padStart(2, '0')}`;
}

// 'YYYY-MM' → { term, monthIdx }
function yearMonthToFiscal(ym: string): { term: number; monthIdx: number } {
  const [y, m] = ym.split('-').map(Number);
  // calendar (y,m) → fiscal: 10,11,12月 は同年期, 1〜9月 は前年期。
  const startY = m >= 10 ? y : y - 1;
  const term = 80 - (2025 - startY);
  const monthIdx = m >= 10 ? m - 10 : m + 2; // 10→0,11→1,12→2,1→3,...9→11
  return { term, monthIdx };
}

function currentYearMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// 進行中(=当月以降)の月かどうか。確定月のみ生成推奨のための警告判定。
function isUnfinalizedMonth(ym: string): boolean {
  return ym >= currentYearMonth();
}

// ---------------------------------------------------------------------------
// 仕訳種(約22 kind)の設計順 + 表示グループ。yamashiroya_branch は依存上 最後。
// ---------------------------------------------------------------------------
interface KindMeta { kind: string; label: string; group: string; }
const KIND_ORDER: KindMeta[] = [
  // 定額
  { kind: 'depreciation', label: '減価償却(期中定額)', group: '定額' },
  { kind: 'locker_rent', label: '更衣室家賃振替', group: '定額' },
  { kind: 'fire_insurance', label: '火災・休業・動産保険(定額)', group: '定額' },
  { kind: 'market_rent', label: '市場家賃振替(定額)', group: '定額' },
  // 在庫
  { kind: 'inventory_tobacco', label: '煙草在庫', group: '在庫' },
  { kind: 'inventory_wine_closing', label: 'ワイン在庫(期末)', group: '在庫' },
  { kind: 'inventory_wine_opening', label: 'ワイン在庫(期首・反転)', group: '在庫' },
  { kind: 'inventory_fresh_pasta_closing', label: '生パスタ等在庫(期末)', group: '在庫' },
  { kind: 'inventory_fresh_pasta_opening', label: '生パスタ等在庫(期首・反転)', group: '在庫' },
  { kind: 'inventory_store_closing', label: '店舗在庫(期末)', group: '在庫' },
  { kind: 'inventory_store_opening', label: '店舗在庫(期首・反転)', group: '在庫' },
  // 按分
  { kind: 'hq_distribution_supplies', label: '本部配布消耗品', group: '按分' },
  { kind: 'hq_distribution_food', label: '本部配布食材', group: '按分' },
  { kind: 'inter_store_transfer', label: '店舗間移動', group: '按分' },
  { kind: 'loss', label: 'ロス金額振替', group: '按分' },
  { kind: 'trial_cost', label: '試作研究費振替', group: '按分' },
  // ヘルプ
  { kind: 'help_allocation', label: 'ヘルプ按分(ROUMU+ARICA)', group: 'ヘルプ' },
  // 前払
  { kind: 'prepaid_cic', label: 'CIC前払費用振替', group: '前払' },
  { kind: 'prepaid_takumiya', label: '匠家前払費用振替', group: '前払' },
  // 社員割引
  { kind: 'employee_discount', label: '社員割引→福利厚生費', group: '社員割引' },
  // 原価率(依存・最後)
  { kind: 'yamashiroya_branch', label: '山城屋庄藏→分店振替(原価率)', group: '原価率(最後に算出)' },
];
const KIND_INDEX: Record<string, number> = KIND_ORDER.reduce((acc, k, i) => { acc[k.kind] = i; return acc; }, {} as Record<string, number>);
const KIND_LABEL: Record<string, string> = KIND_ORDER.reduce((acc, k) => { acc[k.kind] = k.label; return acc; }, {} as Record<string, string>);

// ---------------------------------------------------------------------------
// 設定 setting_key カタログ
//   carry: 期初/契約更新時に値登録 (effective_year_month で世代管理、繰越型)
//   exact: 対象月ごとに実額入力 (effective_year_month = 対象月)
// ---------------------------------------------------------------------------
interface SettingMeta { key: string; label: string; type: 'carry' | 'exact'; note: string; }
const SETTING_CATALOG: SettingMeta[] = [
  { key: 'depreciation', label: '減価償却(期中月額・section別)', type: 'carry', note: '期初に年間見積÷12を固定。期初切替時のみ effective_year_month を新期初に。' },
  { key: 'depreciation_year_end_actual', label: '減価償却(期末実額・台帳)', type: 'carry', note: '期末9月の固定資産台帳実額 (adj=true)。API無し→手入力。' },
  { key: 'fire_insurance', label: '火災・休業・動産保険', type: 'carry', note: '年払÷12。契約更新で総額変更時のみ新値登録。' },
  { key: 'market_rent', label: '市場家賃', type: 'carry', note: '店別想定家賃。実家賃改定時のみ変更。' },
  { key: 'locker_rent', label: '更衣室家賃', type: 'carry', note: '月¥170,000固定按分。最も安定。' },
  { key: 'prepaid_cic', label: 'CIC前払費用(月次配賦)', type: 'carry', note: '本部一括前払の年契約を月次で各店へ配賦。契約改定時のみ。' },
  { key: 'inventory_tobacco', label: '煙草在庫(期末)', type: 'exact', note: '対象月末の煙草実棚卸額 (closing_amount)。' },
  { key: 'inventory_fresh_pasta', label: '生パスタ等在庫(期末)', type: 'exact', note: '対象月末の本部商品(品目別)実棚卸。' },
  { key: 'inventory_store_packaging', label: '店舗包装材在庫(期末)', type: 'exact', note: '対象月末の店舗包装材実棚卸。' },
];

// ---------------------------------------------------------------------------
// status chip 色
// ---------------------------------------------------------------------------
// error / needs_user_input は統一語彙 (lib/statusVocab.ts §正規形) — どちらも「あなたの番」= 橙バッジ。
// 失敗の中身 (error_message) は赤の事実テキストとして行内に別掲される。
const STATUS_LABEL: Record<MonthEndJournalStatus, string> = {
  draft: '下書き',
  suggested: '生成済',
  proposed: '提案',
  approved: '承認済',
  registered: '登録済',
  rejected: '却下',
  error: '対応待ち(処理失敗)',
  needs_user_input: '対応待ち(入力)',
};
const STATUS_STYLE: Record<MonthEndJournalStatus, { bg: string; color: string }> = {
  draft: { bg: '#f5f3f2', color: '#8a8580' },
  suggested: { bg: '#e8f0fe', color: '#2e7cf6' },
  proposed: { bg: '#e8f0fe', color: '#2e7cf6' },
  approved: { bg: '#e6f4ea', color: '#338650' },
  registered: { bg: '#d3efdc', color: '#239e5b' },
  rejected: { bg: '#f5f3f2', color: '#8a8580' },
  error: { bg: UNIFIED_COLOR.action_required.bg, color: UNIFIED_COLOR.action_required.fg },
  needs_user_input: { bg: UNIFIED_COLOR.action_required.bg, color: UNIFIED_COLOR.action_required.fg },
};

const fmt = (n: number | null | undefined) =>
  n == null ? '—' : (n < 0 ? '-' : '') + '¥' + Math.abs(n).toLocaleString('ja-JP');

const TAX_NAME: Record<number, string> = {
  2: '対象外',
  37: '非課仕入',
  108: '課対仕入(軽)8%',
  136: '課対仕入10%',
};
const taxName = (code: number | null | undefined) =>
  code == null ? '—' : (TAX_NAME[code] || `税${code}`);

function parseLines(j: MonthEndJournal): MonthEndJournalLine[] {
  if (Array.isArray(j.lines)) return j.lines as MonthEndJournalLine[];
  if (typeof j.lines === 'string') {
    try { const p = JSON.parse(j.lines); return Array.isArray(p) ? p : []; } catch { return []; }
  }
  return [];
}

// ---------------------------------------------------------------------------
// styles
// ---------------------------------------------------------------------------
const S = {
  page: { background: '#f7f5f5', minHeight: '100vh', paddingBottom: '4rem' } as React.CSSProperties,
  badge: (bg: string, color: string): React.CSSProperties => ({
    display: 'inline-block', fontSize: '0.6875rem', fontWeight: 700, padding: '2px 9px',
    borderRadius: '10px', lineHeight: 1.4, background: bg, color, whiteSpace: 'nowrap',
  }),
  groupRow: {
    background: '#efeeec', padding: '6px 12px', fontSize: '0.6875rem', fontWeight: 700,
    color: '#6b6560', letterSpacing: '0.04em',
  } as React.CSSProperties,
  th: {
    padding: '9px 10px', fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560',
    letterSpacing: '0.03em', borderBottom: '2px solid #dbd9d7', background: '#fafaf8',
    textAlign: 'left' as const, whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  td: {
    padding: '10px 10px', fontSize: '0.8125rem', borderBottom: '1px solid #edebea',
    verticalAlign: 'middle' as const,
  } as React.CSSProperties,
  amt: { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const, whiteSpace: 'nowrap' as const, fontWeight: 600 } as React.CSSProperties,
  detailTh: {
    padding: '5px 8px', fontSize: '0.625rem', fontWeight: 700, color: '#8a8580',
    borderBottom: '1px solid #dbd9d7', background: '#f7f5f5', whiteSpace: 'nowrap' as const, textAlign: 'left' as const,
  } as React.CSSProperties,
  detailTd: {
    padding: '6px 8px', fontSize: '0.75rem', borderBottom: '1px solid #edebea', verticalAlign: 'top' as const,
  } as React.CSSProperties,
  modalOverlay: {
    position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.42)', zIndex: 1000,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' as const,
  } as React.CSSProperties,
  modalCard: {
    background: '#fff', borderRadius: '10px', maxWidth: '880px', width: '100%',
    boxShadow: '0 8px 32px rgba(0,0,0,0.24)', overflow: 'hidden',
  } as React.CSSProperties,
};

interface Toast { id: number; kind: 'success' | 'error' | 'notice' | 'info'; text: string; }

// ---------------------------------------------------------------------------
// hash: #month_end/YYYY-MM
// ---------------------------------------------------------------------------
function parseHashYearMonth(): string | null {
  if (typeof window === 'undefined') return null;
  const m = window.location.hash.toLowerCase().match(/month_end\/(\d{4})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}` : null;
}

// ===========================================================================
// Component
// ===========================================================================
export const MonthEndJournalPage: React.FC = () => {
  const [yearMonth, setYearMonth] = useState<string>(() => parseHashYearMonth() || '2026-03');
  const fiscal = useMemo(() => yearMonthToFiscal(yearMonth), [yearMonth]);

  const [journals, setJournals] = useState<MonthEndJournal[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [metaOpenId, setMetaOpenId] = useState<number | null>(null);
  const [workingId, setWorkingId] = useState<number | null>(null);

  const [sectionsMap, setSectionsMap] = useState<Map<number, string>>(new Map());
  const [accountItemsMap, setAccountItemsMap] = useState<Map<number, string>>(new Map());
  const [itemsMap, setItemsMap] = useState<Map<number, string>>(new Map());
  const [partnersMap, setPartnersMap] = useState<Map<number, string>>(new Map());

  // approve/register chain dialog
  const [dryRun, setDryRun] = useState<MonthEndApproveDryRun | null>(null);
  const [dryRunJournal, setDryRunJournal] = useState<MonthEndJournal | null>(null);
  const [registering, setRegistering] = useState(false);

  // 一括承認・登録
  const [bulkDry, setBulkDry] = useState<MonthEndBulkDryRun | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<MonthEndBulkResult | null>(null);

  // settings panel
  const [showSettings, setShowSettings] = useState(false);

  // toasts
  const [toasts, setToasts] = useState<Toast[]>([]);
  const pushToast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), 7000);
  }, []);

  // sections map (section_id → name) を 1 回取得 (line に section_name が無い時の補完)
  useEffect(() => {
    let alive = true;
    fetchSectionsMap().then((m) => { if (alive) setSectionsMap(m); }).catch(() => {});
    fetchAccountItemsMap().then((m) => { if (alive) setAccountItemsMap(m); }).catch(() => {});
    fetchItemsMap().then((m) => { if (alive) setItemsMap(m); }).catch(() => {});
    fetchPartnersMap().then((m) => { if (alive) setPartnersMap(m); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const sectionName = useCallback(
    (id: number | null | undefined, fallback?: string | null): string => {
      if (fallback) return fallback;
      if (id == null) return '—';
      return sectionsMap.get(id) || `section:${id}`;
    },
    [sectionsMap],
  );
  const accountName = useCallback(
    (id: number | null | undefined, fallback?: string | null): string => {
      if (fallback) return fallback;
      if (id == null) return '—';
      return accountItemsMap.get(id) || `#${id}`;
    },
    [accountItemsMap],
  );
  const itemName = useCallback(
    (id: number | null | undefined, fallback?: string | null): string => {
      if (fallback) return fallback;
      if (id == null) return '';
      return itemsMap.get(id) || `#${id}`;
    },
    [itemsMap],
  );
  const partnerName = useCallback(
    (id: number | null | undefined, fallback?: string | null): string => {
      if (fallback) return fallback;
      if (id == null) return '';
      return partnersMap.get(id) || `#${id}`;
    },
    [partnersMap],
  );

  // -------------------------------------------------------------------------
  // load
  // -------------------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMonthEndJournals(yearMonth);
      const items = (res.items || []).slice().sort((a, b) => {
        const ia = KIND_INDEX[a.journal_kind] ?? 999;
        const ib = KIND_INDEX[b.journal_kind] ?? 999;
        if (ia !== ib) return ia - ib;
        return (a.title || '').localeCompare(b.title || '');
      });
      setJournals(items);
      setLastLoadedAt(new Date().toISOString().slice(0, 19).replace('T', ' '));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [yearMonth]);

  useEffect(() => { load(); }, [load]);

  // -------------------------------------------------------------------------
  // generate (全 kind)
  // -------------------------------------------------------------------------
  const onGenerate = useCallback(async () => {
    if (!window.confirm(`${yearMonth} の月末仕訳を全 kind 生成/再生成します。確定月のデータを使用します。よろしいですか?`)) return;
    setGenerating(true);
    try {
      const res = await generateMonthEndJournals(yearMonth);
      const n = res.generated ?? (Array.isArray(res.results) ? res.results.length : undefined);
      pushToast({ kind: 'success', text: `${yearMonth} 生成完了${n != null ? ` (${n} kind)` : ''}` });
      await load();
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: `生成失敗: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setGenerating(false);
    }
  }, [yearMonth, pushToast, load]);

  // -------------------------------------------------------------------------
  // 承認・登録 chain: dry_run preview → 確認ダイアログ
  // -------------------------------------------------------------------------
  const openApproveDialog = useCallback(async (j: MonthEndJournal) => {
    setWorkingId(j.id);
    try {
      const dr = await approveMonthEndJournalDryRun(j.id);
      setDryRun(dr);
      setDryRunJournal(j);
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: `検証失敗: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setWorkingId(null);
    }
  }, [pushToast]);

  // freee に登録 (dry_run:false) — UIクリック起点で直接 fetch (auto-mode 分類器対象外)
  const onConfirmRegister = useCallback(async () => {
    if (!dryRunJournal) return;
    const j = dryRunJournal;
    setRegistering(true);
    try {
      let sid = '';
      try {
        const sess = await openSessionForMonthEnd();
        if (sess && sess.session_id) sid = sess.session_id;
      } catch (e) {
        pushToast({ kind: 'error', text: `session open 失敗: ${e instanceof Error ? e.message : String(e)}` });
        setRegistering(false);
        return;
      }
      const res = await approveMonthEndJournal(j.id, sid);
      if (res.ok && (res.status === 'registered' || res.freee_mj_id)) {
        pushToast({ kind: 'success', text: `${KIND_LABEL[j.journal_kind] || j.journal_kind}: freee 登録完了 (MJ ${res.freee_mj_id ?? '—'})` });
        setDryRun(null);
        setDryRunJournal(null);
        await load();
      } else {
        pushToast({ kind: 'error', text: `登録失敗: ${res.error || res.errors?.join(' / ') || JSON.stringify(res)}` });
      }
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: `登録失敗: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setRegistering(false);
    }
  }, [dryRunJournal, pushToast, load]);

  // -------------------------------------------------------------------------
  // 一括承認・登録
  // -------------------------------------------------------------------------
  const onBulkPreview = useCallback(async () => {
    setBulkBusy(true);
    try {
      const res = await bulkApproveMonthEndDryRun(yearMonth);
      setBulkDry(res);
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: `一括プレビュー失敗: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setBulkBusy(false);
    }
  }, [yearMonth, pushToast]);

  const onBulkConfirm = useCallback(async () => {
    setBulkBusy(true);
    try {
      let sid = '';
      try {
        const sess = await openSessionForMonthEnd();
        if (sess && sess.session_id) sid = sess.session_id;
      } catch (e) {
        pushToast({ kind: 'error', text: `session open 失敗: ${e instanceof Error ? e.message : String(e)}` });
        setBulkBusy(false);
        return;
      }
      const res = await bulkApproveMonthEnd(yearMonth, sid);
      setBulkResult(res);
      setBulkDry(null);
      pushToast({
        kind: res.failed_count > 0 ? 'error' : 'success',
        text: `一括登録: ${res.registered_count}件 登録完了${res.failed_count > 0 ? ` / 失敗 ${res.failed_count}件` : ''}`,
      });
      await load();
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: `一括登録失敗: ${e instanceof Error ? e.message : String(e)} (一部登録済の可能性 → 再読込で確認)` });
    } finally {
      setBulkBusy(false);
    }
  }, [yearMonth, pushToast, load]);

  // -------------------------------------------------------------------------
  // reject
  // -------------------------------------------------------------------------
  const onReject = useCallback(async (j: MonthEndJournal) => {
    const reason = window.prompt(`${KIND_LABEL[j.journal_kind] || j.journal_kind} を却下します。理由 (任意):`, '');
    if (reason === null) return;
    setWorkingId(j.id);
    try {
      let sid = '';
      try { const s = await openSessionForMonthEnd(); sid = s.session_id; } catch { /* worker fallback */ }
      await rejectMonthEndJournal(j.id, reason, sid);
      pushToast({ kind: 'info', text: `却下しました` });
      await load();
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: `却下失敗: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setWorkingId(null);
    }
  }, [pushToast, load]);

  // -------------------------------------------------------------------------
  // selectors
  // -------------------------------------------------------------------------
  const termOptions = TERMS.map((t) => ({ name: `${t}期`, value: String(t) }));
  const monthOptions = FISCAL_MONTH_LABEL.map((lbl, i) => ({ name: lbl, value: String(i) }));

  const onTermChange = (term: number) => {
    const ym = fiscalToYearMonth(term, fiscal.monthIdx);
    setYearMonth(ym);
    window.location.hash = `#month_end/${ym}`;
  };
  const onMonthChange = (monthIdx: number) => {
    const ym = fiscalToYearMonth(fiscal.term, monthIdx);
    setYearMonth(ym);
    window.location.hash = `#month_end/${ym}`;
  };

  // -------------------------------------------------------------------------
  // stats
  // -------------------------------------------------------------------------
  const stats = useMemo(() => {
    const total = journals.length;
    const registered = journals.filter((j) => j.status === 'registered').length;
    const blocked = journals.filter((j) => j.status === 'needs_user_input' || j.status === 'error').length;
    const unbalanced = journals.filter((j) => j.balanced === 0).length;
    return { total, registered, blocked, unbalanced };
  }, [journals]);

  const unfinalized = isUnfinalizedMonth(yearMonth);

  // 登録ボタンの有効/無効判定
  const canRegister = (j: MonthEndJournal): { ok: boolean; reason?: string } => {
    if (j.status === 'registered') return { ok: false, reason: '登録済' };
    if (j.status === 'needs_user_input') return { ok: false, reason: '入力待ち (設定パネルで解消)' };
    if (j.status === 'error') return { ok: false, reason: 'エラー' };
    if (j.balanced === 0) return { ok: false, reason: '借貸不一致' };
    return { ok: true };
  };

  // -------------------------------------------------------------------------
  // render: drilldown (借方/貸方 split)
  // -------------------------------------------------------------------------
  const renderDrilldown = (j: MonthEndJournal) => {
    const lines = parseLines(j);
    const debits = lines.filter((l) => l.entry_side === 'debit');
    const credits = lines.filter((l) => l.entry_side === 'credit');
    const diff = (j.total_debit || 0) - (j.total_credit || 0);

    const renderSide = (label: string, side: MonthEndJournalLine[], totalLabel: number) => (
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: side === debits ? '#2667d4' : '#c4732a', marginBottom: '4px' }}>
          {label} ({side.length}行)
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={S.detailTh}>勘定科目</th>
              <th style={S.detailTh}>部門</th>
              <th style={S.detailTh}>品目</th>
              <th style={S.detailTh}>取引先</th>
              <th style={S.detailTh}>税</th>
              <th style={{ ...S.detailTh, textAlign: 'right' }}>金額</th>
              <th style={{ ...S.detailTh, textAlign: 'right' }}>vat</th>
            </tr>
          </thead>
          <tbody>
            {side.map((l, i) => (
              <tr key={i}>
                <td style={{ ...S.detailTd, fontWeight: 600, color: '#252525' }}>
                  {accountName(l.account_item_id, l.account_name)}
                  {l.description ? <div style={{ fontWeight: 400, color: '#8a8580', fontSize: '0.6875rem' }}>{l.description}</div> : null}
                </td>
                <td style={S.detailTd}>{sectionName(l.section_id, l.section_name)}</td>
                <td style={S.detailTd}>{(l.item_id || l.item_name) ? itemName(l.item_id, l.item_name) : <span style={{ color: '#c4c0bc' }}>—</span>}</td>
                <td style={S.detailTd}>{(l.partner_id || l.partner_name) ? partnerName(l.partner_id, l.partner_name) : <span style={{ color: '#c4c0bc' }}>—</span>}</td>
                <td style={S.detailTd}><span style={S.badge('#f5f3f2', '#6b6560')}>{taxName(l.tax_code)}</span></td>
                <td style={{ ...S.detailTd, ...S.amt }}>{fmt(l.amount)}</td>
                <td style={{ ...S.detailTd, ...S.amt, color: '#8a8580' }}>{l.vat ? fmt(l.vat) : '—'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} style={{ ...S.detailTd, fontWeight: 700, background: '#f7f5f5' }}>{label}計</td>
              <td style={{ ...S.detailTd, ...S.amt, background: '#f7f5f5', fontWeight: 700 }}>{fmt(totalLabel)}</td>
              <td style={{ ...S.detailTd, background: '#f7f5f5' }} />
            </tr>
          </tfoot>
        </table>
      </div>
    );

    return (
      <div style={{ padding: '14px 18px', background: '#fafaf8' }}>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {renderSide('借方', debits, j.total_debit)}
          {renderSide('貸方', credits, j.total_credit)}
        </div>
        <div style={{ marginTop: '8px', display: 'flex', gap: '16px', alignItems: 'center' }}>
          <span style={S.badge(j.balanced ? '#e6f4ea' : '#fdeaea', j.balanced ? '#338650' : '#c33939')}>
            {j.balanced ? '借貸一致' : `借貸不一致 (差額 ${fmt(diff)})`}
          </span>
          <Text size={0.75} color="burnt">借方計 {fmt(j.total_debit)} / 貸方計 {fmt(j.total_credit)}</Text>
        </div>
        {j.error_message && (
          <div style={{ marginTop: '8px' }}>
            <MessageBlock error>{j.error_message}</MessageBlock>
          </div>
        )}
        {/* source_meta 折りたたみ */}
        <div style={{ marginTop: '10px' }}>
          <Button small appearance="tertiary" onClick={() => setMetaOpenId(metaOpenId === j.id ? null : j.id)}>
            {metaOpenId === j.id ? '算定根拠 (source_meta) を隠す ▲' : '算定根拠 (source_meta) を表示 ▼'}
          </Button>
          {metaOpenId === j.id && (
            <pre style={{
              marginTop: '6px', padding: '10px 12px', background: '#f7f5f5', borderRadius: '6px',
              fontSize: '0.6875rem', color: '#6b6560', whiteSpace: 'pre-wrap', overflowX: 'auto', maxHeight: '320px',
            }}>
              {JSON.stringify(j.source_meta ?? {}, null, 2)}
            </pre>
          )}
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // render
  // -------------------------------------------------------------------------
  return (
    <div style={S.page}>
      <Container width="wide">
        <div style={{ padding: '16px 0 6px' }}>
          <Breadcrumbs links={[{ title: 'FREEEMAN', url: '/' }, { title: '月末仕訳' }, { title: yearMonth }]} />
        </div>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '8px' }}>
          <HStack gap={1} alignItems="center">
            <PageTitle>月末仕訳</PageTitle>
            <span style={S.badge('#e8f0fe', '#2e7cf6')}>{fiscal.term}期 {FISCAL_MONTH_LABEL[fiscal.monthIdx]}</span>
            <span style={S.badge('#f0f9ff', '#0284c7')}>{yearMonth}</span>
            {unfinalized && <span style={S.badge('#fff4e5', '#c67320')}>進行中の月: データ未確定の可能性</span>}
          </HStack>
          <HStack gap={0.5} alignItems="end">
            <SelectBox options={termOptions} value={String(fiscal.term)} width="small" small label="会計期"
              onChange={(e) => onTermChange(Number((e.target as HTMLSelectElement).value))} />
            <SelectBox options={monthOptions} value={String(fiscal.monthIdx)} width="small" small label="月度"
              onChange={(e) => onMonthChange(Number((e.target as HTMLSelectElement).value))} />
            <Button appearance="primary" onClick={onGenerate} disabled={generating || loading}>
              {generating ? <><InlineSpinner isLoading /> 生成中…</> : '生成 (全 kind)'}
            </Button>
            <Button appearance="secondary" small onClick={load} disabled={loading}>
              {loading ? '読込中…' : '再読込'}
            </Button>
            <Button appearance="primary" onClick={onBulkPreview} disabled={loading || bulkBusy || generating}>
              {bulkBusy ? <><InlineSpinner isLoading /> 処理中…</> : '一括承認・登録'}
            </Button>
            <Button appearance="tertiary" small onClick={() => setShowSettings((v) => !v)}>
              {showSettings ? '設定を閉じる' : '設定パネル'}
            </Button>
          </HStack>
        </div>

        <Note>
          確定月のみを対象に「生成」してください。KOTATSU / ROUMU / ARICA は月締め後にデータ確定します。
          {lastLoadedAt && ` / 最終更新 ${lastLoadedAt}`}
          {` / 全${stats.total}件 / 登録済${stats.registered} / 対応待ち${stats.blocked} (入力/処理失敗 — この表で解消) / 借貸不一致${stats.unbalanced}`}
        </Note>

        {error && <div style={{ marginTop: '10px' }}><MessageBlock error>{error}</MessageBlock></div>}

        {/* 設定パネル */}
        {showSettings && (
          <div style={{ marginTop: '14px' }}>
            <SettingsPanel
              yearMonth={yearMonth}
              sectionName={sectionName}
              accountName={accountName}
              pushToast={pushToast}
            />
          </div>
        )}

        {/* 仕訳一覧 */}
        <div style={{ marginTop: '14px' }}>
          <CardBase paddingSize="zero" overflowHidden={false}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                <thead>
                  <tr>
                    <th style={S.th}>仕訳種</th>
                    <th style={{ ...S.th, textAlign: 'center', width: '90px' }}>ステータス</th>
                    <th style={{ ...S.th, textAlign: 'right', width: '120px' }}>借方計</th>
                    <th style={{ ...S.th, textAlign: 'right', width: '120px' }}>貸方計</th>
                    <th style={{ ...S.th, textAlign: 'center', width: '80px' }}>借貸</th>
                    <th style={{ ...S.th, textAlign: 'center', width: '120px' }}>freee MJ</th>
                    <th style={{ ...S.th, textAlign: 'center', width: '210px' }}>アクション</th>
                    <th style={{ ...S.th, textAlign: 'center', width: '40px' }} />
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rows: React.ReactNode[] = [];
                    let lastGroup = '';
                    for (const j of journals) {
                      const meta = KIND_ORDER.find((k) => k.kind === j.journal_kind);
                      const group = meta?.group || 'その他';
                      if (group !== lastGroup) {
                        rows.push(
                          <tr key={`g_${group}`}>
                            <td colSpan={8} style={S.groupRow}>{group}</td>
                          </tr>,
                        );
                        lastGroup = group;
                      }
                      const st = STATUS_STYLE[j.status] || STATUS_STYLE.draft;
                      const reg = canRegister(j);
                      const isLast = j.journal_kind === 'yamashiroya_branch';
                      rows.push(
                        <React.Fragment key={j.id}>
                          <tr style={{ cursor: 'pointer', background: expandedId === j.id ? '#ebf3ff' : 'transparent' }}
                            onClick={() => setExpandedId(expandedId === j.id ? null : j.id)}>
                            <td style={S.td}>
                              <span style={{ fontWeight: 600, color: '#252525' }}>{KIND_LABEL[j.journal_kind] || j.title || j.journal_kind}</span>
                              {isLast && (
                                <div style={{ marginTop: '2px' }}>
                                  <span style={S.badge('#fff4e5', '#c67320')}>他の月末仕訳を登録後に生成/再生成</span>
                                </div>
                              )}
                              {(j.status === 'needs_user_input' || j.status === 'error') && j.error_message && (
                                <div style={{ marginTop: '2px', fontSize: '0.6875rem', color: '#c33939' }}>{j.error_message}</div>
                              )}
                            </td>
                            <td style={{ ...S.td, textAlign: 'center' }}>
                              <span style={S.badge(st.bg, st.color)}>{STATUS_LABEL[j.status] || j.status}</span>
                            </td>
                            <td style={{ ...S.td, ...S.amt }}>{fmt(j.total_debit)}</td>
                            <td style={{ ...S.td, ...S.amt }}>{fmt(j.total_credit)}</td>
                            <td style={{ ...S.td, textAlign: 'center' }}>
                              <span style={S.badge(j.balanced ? '#e6f4ea' : '#fdeaea', j.balanced ? '#338650' : '#c33939')}>
                                {j.balanced ? '一致' : '不一致'}
                              </span>
                            </td>
                            <td style={{ ...S.td, textAlign: 'center' }}>
                              {j.freee_mj_id ? (
                                <a href={`https://secure.freee.co.jp/reports/journals`} target="_blank" rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()} style={{ ...S.badge('#d3efdc', '#239e5b'), textDecoration: 'none' }}>
                                  MJ {j.freee_mj_id} ↗
                                </a>
                              ) : <span style={{ color: '#c4c0bc' }}>—</span>}
                            </td>
                            <td style={{ ...S.td, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                              <HStack gap={0.25} alignItems="center">
                                <Button small appearance="primary"
                                  disabled={!reg.ok || workingId === j.id}
                                  onClick={() => openApproveDialog(j)}>
                                  {workingId === j.id ? '…' : '承認・登録'}
                                </Button>
                                <Button small appearance="tertiary" danger
                                  disabled={j.status === 'registered' || workingId === j.id}
                                  onClick={() => onReject(j)}>
                                  却下
                                </Button>
                              </HStack>
                              {!reg.ok && reg.reason && j.status !== 'registered' && (
                                <div style={{ fontSize: '0.625rem', color: '#c33939', marginTop: '2px' }}>{reg.reason}</div>
                              )}
                            </td>
                            <td style={{ ...S.td, textAlign: 'center' }} onClick={(e) => { e.stopPropagation(); setExpandedId(expandedId === j.id ? null : j.id); }}>
                              <span style={{ color: '#8a8580', transform: expandedId === j.id ? 'rotate(180deg)' : 'none', display: 'inline-block', transition: 'transform 0.2s' }}>&#9660;</span>
                            </td>
                          </tr>
                          {expandedId === j.id && (
                            <tr>
                              <td colSpan={8} style={{ padding: 0 }}>{renderDrilldown(j)}</td>
                            </tr>
                          )}
                        </React.Fragment>,
                      );
                    }
                    return rows;
                  })()}

                  {journals.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ padding: '48px 24px', textAlign: 'center' }}>
                        <VStack gap={0.5} alignItems="center">
                          <Text size={0.875} color="burnt">{loading ? '読込中…' : `${yearMonth} の月末仕訳はまだありません`}</Text>
                          <Note>「生成 (全 kind)」を押すと対象月の全仕訳を生成します</Note>
                        </VStack>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBase>
        </div>
      </Container>

      {/* 承認・登録 確認ダイアログ (dry_run preview) */}
      {dryRun && dryRunJournal && (
        <ApproveDialog
          dryRun={dryRun}
          journal={dryRunJournal}
          sectionName={sectionName}
          accountName={accountName}
          itemName={itemName}
          partnerName={partnerName}
          registering={registering}
          onCancel={() => { if (!registering) { setDryRun(null); setDryRunJournal(null); } }}
          onConfirm={onConfirmRegister}
        />
      )}

      {/* 一括承認・登録 確認ダイアログ (dry_run preview) */}
      {bulkDry && (
        <div style={S.modalOverlay} onClick={() => { if (!bulkBusy) setBulkDry(null); }}>
          <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 22px', borderBottom: '1px solid #edebea' }}>
              <SectionTitle headlineLevel={3}>一括承認・登録 — {bulkDry.year_month}</SectionTitle>
              <Note>提案済(借貸一致)の仕訳をまとめて freee 振替伝票に登録します。</Note>
            </div>
            <div style={{ padding: '16px 22px', maxHeight: '60vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <span style={S.badge('#e6f4ea', '#338650')}>登録対象 {bulkDry.eligible_count}件</span>
                <span style={S.badge('#f0f9ff', '#0284c7')}>合計借方 {fmt(bulkDry.grand_total_debit)}</span>
                {Object.keys(bulkDry.not_eligible || {}).length > 0 && (
                  <span style={S.badge('#fff4e5', '#c67320')}>
                    対象外 {Object.entries(bulkDry.not_eligible).map(([s, n]) => `${STATUS_LABEL[s as MonthEndJournalStatus] || s}${n}`).join(' / ')} (未確定/0円は自動除外)
                  </span>
                )}
              </div>
              {bulkDry.has_yamashiroya && (
                <div style={{ marginBottom: '10px' }}>
                  <Note>※ 山城屋庄藏→分店(原価率)は他仕訳の登録後に再生成して最後に登録されます。</Note>
                </div>
              )}
              {bulkDry.eligible_count === 0 ? (
                <MessageBlock>登録対象(提案済・借貸一致)の仕訳がありません。</MessageBlock>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr><th style={S.detailTh}>仕訳種</th><th style={{ ...S.detailTh, textAlign: 'right' }}>借方計</th></tr>
                  </thead>
                  <tbody>
                    {bulkDry.items.map((it) => (
                      <tr key={it.id}>
                        <td style={S.detailTd}>{KIND_LABEL[it.journal_kind] || it.title || it.journal_kind}</td>
                        <td style={{ ...S.detailTd, ...S.amt }}>{fmt(it.total_debit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid #edebea', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Note>登録は freee への実書込です (session lease 必須)。{bulkDry.eligible_count}件で20〜40秒かかる場合があります。</Note>
              <HStack gap={0.5}>
                <Button appearance="tertiary" onClick={() => setBulkDry(null)} disabled={bulkBusy}>キャンセル</Button>
                <Button appearance="primary" onClick={onBulkConfirm} disabled={bulkBusy || bulkDry.eligible_count === 0}>
                  {bulkBusy ? <><InlineSpinner isLoading /> 一括登録中…</> : `一括でfreeeに登録 ▸ (${bulkDry.eligible_count}件)`}
                </Button>
              </HStack>
            </div>
          </div>
        </div>
      )}

      {/* 一括登録 結果 */}
      {bulkResult && (
        <div style={S.modalOverlay} onClick={() => setBulkResult(null)}>
          <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '16px 22px', borderBottom: '1px solid #edebea' }}>
              <SectionTitle headlineLevel={3}>一括登録 結果 — {bulkResult.year_month}</SectionTitle>
            </div>
            <div style={{ padding: '16px 22px', maxHeight: '60vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <span style={S.badge('#e6f4ea', '#338650')}>登録完了 {bulkResult.registered_count}件</span>
                {bulkResult.failed_count > 0 && <span style={S.badge('#fdeaea', '#c33939')}>失敗 {bulkResult.failed_count}件</span>}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={S.detailTh}>仕訳種</th><th style={S.detailTh}>結果</th><th style={S.detailTh}>freee MJ / 詳細</th></tr></thead>
                <tbody>
                  {bulkResult.results.map((r) => (
                    <tr key={r.id}>
                      <td style={S.detailTd}>{KIND_LABEL[r.journal_kind] || r.journal_kind}{r.regenerated ? ' (再生成)' : ''}</td>
                      <td style={S.detailTd}>
                        <span style={S.badge(r.ok ? '#e6f4ea' : '#fdeaea', r.ok ? '#338650' : '#c33939')}>{r.ok ? '登録' : (r.skipped ? 'skip' : '失敗')}</span>
                      </td>
                      <td style={{ ...S.detailTd, color: r.ok ? '#6b6560' : '#c33939', fontSize: '0.6875rem' }}>
                        {r.ok ? (r.freee_mj_id ? `MJ ${r.freee_mj_id}` : '—') : (r.error || r.errors?.join(' / ') || r.reason || '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid #edebea', display: 'flex', justifyContent: 'flex-end' }}>
              <Button appearance="primary" onClick={() => setBulkResult(null)}>閉じる</Button>
            </div>
          </div>
        </div>
      )}

      {/* toasts */}
      {toasts.map((t) => (
        <FloatingMessageBlock key={t.id}
          success={t.kind === 'success'} error={t.kind === 'error'} notice={t.kind === 'notice'} info={t.kind === 'info'}
          onClose={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}>
          {t.text}
        </FloatingMessageBlock>
      ))}
    </div>
  );
};

// ===========================================================================
// 承認・登録ダイアログ — dry_run の送信予定 payload + Σ検算 を表示し、
//   「freeeに登録 ▸」で onConfirm (= openSession → dry_run:false fetch) を起動。
// ===========================================================================
const ApproveDialog: React.FC<{
  dryRun: MonthEndApproveDryRun;
  journal: MonthEndJournal;
  sectionName: (id: number | null | undefined, fallback?: string | null) => string;
  accountName: (id: number | null | undefined, fallback?: string | null) => string;
  itemName: (id: number | null | undefined, fallback?: string | null) => string;
  partnerName: (id: number | null | undefined, fallback?: string | null) => string;
  registering: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ dryRun, journal, sectionName, accountName, itemName, partnerName, registering, onCancel, onConfirm }) => {
  const v = dryRun.validation;
  const details = dryRun.payload?.details || [];
  const debits = details.filter((d) => d.entry_side === 'debit');
  const credits = details.filter((d) => d.entry_side === 'credit');
  const hasErrors = (dryRun.errors && dryRun.errors.length > 0) || !v.balanced || !v.issue_date_ok;

  const renderDetails = (label: string, side: typeof details, color: string) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '0.6875rem', fontWeight: 700, color, marginBottom: '4px' }}>{label} ({side.length}行)</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={S.detailTh}>勘定</th>
            <th style={S.detailTh}>部門</th>
            <th style={S.detailTh}>品目/取引先</th>
            <th style={S.detailTh}>税</th>
            <th style={{ ...S.detailTh, textAlign: 'right' }}>金額</th>
          </tr>
        </thead>
        <tbody>
          {side.map((d, i) => (
            <tr key={i}>
              <td style={S.detailTd}>
                {accountName(d.account_item_id)}
                {d.description ? <div style={{ color: '#8a8580', fontSize: '0.625rem' }}>{d.description}</div> : null}
              </td>
              <td style={S.detailTd}>{d.section_id ? sectionName(d.section_id) : '—'}</td>
              <td style={S.detailTd}>{((d as any).item_id || (d as any).partner_id) ? [(d as any).item_id ? itemName((d as any).item_id) : '', (d as any).partner_id ? partnerName((d as any).partner_id) : ''].filter(Boolean).join(' / ') : <span style={{ color: '#c4c0bc' }}>—</span>}</td>
              <td style={S.detailTd}><span style={S.badge('#f5f3f2', '#6b6560')}>{taxName(d.tax_code)}</span></td>
              <td style={{ ...S.detailTd, ...S.amt }}>{fmt(d.amount)}{d.vat ? <div style={{ color: '#8a8580', fontSize: '0.625rem' }}>vat {fmt(d.vat)}</div> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={S.modalOverlay} onClick={() => { if (!registering) onCancel(); }}>
      <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '16px 22px', borderBottom: '1px solid #edebea' }}>
          <SectionTitle headlineLevel={3}>freee 登録確認 — {KIND_LABEL[journal.journal_kind] || journal.journal_kind}</SectionTitle>
          <Note>{journal.year_month} / 振替伝票 (manual_journals)。下記が freee に送信される予定の内容です。</Note>
        </div>

        <div style={{ padding: '16px 22px', maxHeight: '60vh', overflowY: 'auto' }}>
          {/* Σ検算 + 検証 */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <span style={S.badge(v.balanced ? '#e6f4ea' : '#fdeaea', v.balanced ? '#338650' : '#c33939')}>
              {v.balanced ? `Σ一致: 借${fmt(v.total_debit)} = 貸${fmt(v.total_credit)}` : `Σ不一致: 借${fmt(v.total_debit)} / 貸${fmt(v.total_credit)}`}
            </span>
            <span style={S.badge(v.issue_date_ok ? '#e6f4ea' : '#fdeaea', v.issue_date_ok ? '#338650' : '#c33939')}>
              発生日 {v.issue_date}{v.issue_date_ok ? '' : ` (期待 ${v.expected_issue_date})`}
            </span>
            <span style={S.badge('#f5f3f2', '#6b6560')}>{v.line_count}行</span>
            <span style={S.badge('#f5f3f2', '#6b6560')}>adjustment: {String(dryRun.payload?.adjustment)}</span>
          </div>

          {dryRun.errors && dryRun.errors.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <MessageBlock error>{dryRun.errors.join(' / ')}</MessageBlock>
            </div>
          )}

          <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {renderDetails('借方', debits, '#2667d4')}
            {renderDetails('貸方', credits, '#c4732a')}
          </div>
        </div>

        <div style={{ padding: '14px 22px', borderTop: '1px solid #edebea', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Note>登録は freee 振替伝票への実書込です (session lease 必須)。</Note>
          <HStack gap={0.5}>
            <Button appearance="tertiary" onClick={onCancel} disabled={registering}>キャンセル</Button>
            <Button appearance="primary" onClick={onConfirm} disabled={registering || hasErrors}>
              {registering ? <><InlineSpinner isLoading /> 登録中…</> : 'freeeに登録 ▸'}
            </Button>
          </HStack>
        </div>
      </div>
    </div>
  );
};

// ===========================================================================
// 設定パネル — setting_key 別の手入力 UI。
//   section別 {section_id, amount} の表編集 + 任意の value 形を崩さない raw JSON 編集。
// ===========================================================================
const SettingsPanel: React.FC<{
  yearMonth: string;
  sectionName: (id: number | null | undefined, fallback?: string | null) => string;
  accountName: (id: number | null | undefined, fallback?: string | null) => string;
  pushToast: (t: { kind: 'success' | 'error' | 'notice' | 'info'; text: string }) => void;
}> = ({ yearMonth, sectionName, accountName, pushToast }) => {
  const [activeTab, setActiveTab] = useState(0);
  const meta = SETTING_CATALOG[activeTab];

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [effYm, setEffYm] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [rawJson, setRawJson] = useState<string>('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [loadedFromEff, setLoadedFromEff] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setJsonError(null);
    try {
      const res = await fetchMonthEndSettings(meta.key, yearMonth);
      const item = res.item;
      // exact型は対象月を effective に、carry型は既存 effective を引き継ぐ
      const defaultEff = meta.type === 'exact' ? yearMonth : (item?.effective_year_month || yearMonth);
      setEffYm(defaultEff);
      setNote(item?.note || '');
      setRawJson(item?.value != null ? JSON.stringify(item.value, null, 2) : (meta.key === 'inventory_tobacco' ? '{\n  "closing_amount": 0\n}' : '{\n  "lines": []\n}'));
      setLoadedFromEff(item?.effective_year_month ?? null);
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: `設定取得失敗: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setLoading(false);
    }
  }, [meta.key, meta.type, yearMonth, pushToast]);

  useEffect(() => { load(); }, [load]);

  // parsed value (table view 用)
  const parsed: any = useMemo(() => {
    try { return JSON.parse(rawJson); } catch { return null; }
  }, [rawJson]);

  const linesArr: any[] = Array.isArray(parsed?.lines) ? parsed.lines : [];
  // section別 amount の簡易編集が可能か (= lines が {section_id, amount} 主体)
  const isSectionAmountShape = linesArr.length > 0 && linesArr.every((l) => 'amount' in l) && !('closing_amount' in (parsed || {}));

  const onSave = useCallback(async () => {
    let valueJson: any;
    try {
      valueJson = JSON.parse(rawJson);
    } catch (e) {
      setJsonError('JSON が不正です: ' + (e instanceof Error ? e.message : String(e)));
      return;
    }
    setJsonError(null);
    if (!/^\d{4}-\d{2}$/.test(effYm)) {
      pushToast({ kind: 'error', text: 'effective_year_month は YYYY-MM 形式で入力してください' });
      return;
    }
    setSaving(true);
    try {
      await saveMonthEndSetting({
        setting_key: meta.key,
        effective_year_month: effYm,
        value_json: valueJson,
        note: note || undefined,
      });
      pushToast({ kind: 'success', text: `${meta.label} を保存しました (eff ${effYm})` });
      await load();
    } catch (e: unknown) {
      pushToast({ kind: 'error', text: `保存失敗: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setSaving(false);
    }
  }, [rawJson, effYm, note, meta.key, meta.label, pushToast, load]);

  // section別 table の amount 編集 → rawJson に反映
  const updateLineAmount = (idx: number, amount: number) => {
    if (!parsed) return;
    const next = { ...parsed, lines: linesArr.map((l, i) => (i === idx ? { ...l, amount } : l)) };
    setRawJson(JSON.stringify(next, null, 2));
  };
  // closing_amount (inventory_tobacco) 編集
  const updateClosingAmount = (amount: number) => {
    const next = { ...(parsed || {}), closing_amount: amount };
    setRawJson(JSON.stringify(next, null, 2));
  };

  return (
    <CardBase>
      <div style={{ padding: '16px 18px' }}>
        <SectionTitle headlineLevel={3}>設定パネル — 月末仕訳の手入力値</SectionTitle>
        <Note>
          carry-forward 型は期初/契約更新時に effective_year_month を新値の起点に。
          exact-month 型は対象月 ({yearMonth}) ごとに実額を入力します。
        </Note>

        <div style={{ marginTop: '10px' }}>
          <TabBar
            tabs={SETTING_CATALOG.map((s) => s.label)}
            currentTabIndex={activeTab}
            onClickTab={(i) => setActiveTab(i)}
            small
          />
        </div>

        <div style={{ marginTop: '12px' }}>
          <HStack gap={0.5} alignItems="center">
            <span style={S.badge(meta.type === 'carry' ? '#e8f0fe' : '#fff4e5', meta.type === 'carry' ? '#2e7cf6' : '#c67320')}>
              {meta.type === 'carry' ? '繰越型 (carry-forward)' : '実額型 (exact-month)'}
            </span>
            {loadedFromEff && <Text size={0.75} color="burnt">現行値の effective: {loadedFromEff}</Text>}
            {loading && <InlineSpinner isLoading />}
          </HStack>
          <div style={{ marginTop: '4px' }}><Note>{meta.note}</Note></div>
        </div>

        {/* effective_year_month + note */}
        <div style={{ marginTop: '12px', display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560', marginBottom: '2px' }}>effective_year_month</div>
            <TextField small width="small" value={effYm} placeholder="YYYY-MM"
              onChange={(e: any) => setEffYm(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: '240px' }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560', marginBottom: '2px' }}>note (任意)</div>
            <TextField small width="full" value={note} placeholder="根拠 MJ 等"
              onChange={(e: any) => setNote(e.target.value)} />
          </div>
        </div>

        {/* section別 amount 表 (編集可能な簡易ビュー) */}
        {meta.key === 'inventory_tobacco' ? (
          <div style={{ marginTop: '14px' }}>
            <SubSectionTitle headlineLevel={4}>期末棚卸額</SubSectionTitle>
            <div style={{ marginTop: '6px', maxWidth: '320px' }}>
              <TextField small width="full" value={String(parsed?.closing_amount ?? '')}
                onChange={(e: any) => updateClosingAmount(Number(e.target.value.replace(/[^\d-]/g, '')) || 0)} />
            </div>
          </div>
        ) : isSectionAmountShape ? (
          <div style={{ marginTop: '14px' }}>
            <SubSectionTitle headlineLevel={4}>section別 金額 (編集可)</SubSectionTitle>
            <ColumnBase paddingSize="zero" border="default" rounded>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={S.detailTh}>借貸</th>
                    <th style={S.detailTh}>勘定</th>
                    <th style={S.detailTh}>部門</th>
                    <th style={{ ...S.detailTh, textAlign: 'right', width: '160px' }}>金額</th>
                  </tr>
                </thead>
                <tbody>
                  {linesArr.map((l, i) => (
                    <tr key={i}>
                      <td style={S.detailTd}>{l.entry_side || '—'}</td>
                      <td style={S.detailTd}>{accountName(l.account_item_id, l.account_name)}</td>
                      <td style={S.detailTd}>{sectionName(l.section_id, l.section_name)}</td>
                      <td style={{ ...S.detailTd, textAlign: 'right' }}>
                        <TextField small width="full" value={String(l.amount ?? '')}
                          onChange={(e: any) => updateLineAmount(i, Number(e.target.value.replace(/[^\d-]/g, '')) || 0)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ColumnBase>
          </div>
        ) : (
          <div style={{ marginTop: '12px' }}>
            <Note>この設定は構造が複雑なため下の JSON エディタで編集してください。</Note>
          </div>
        )}

        {/* raw JSON editor (全 value 形を非破壊で編集) */}
        <div style={{ marginTop: '14px' }}>
          <SubSectionTitle headlineLevel={4}>value_json (raw)</SubSectionTitle>
          <TextArea value={rawJson} height={260} width="full"
            onChange={(e: any) => { setRawJson(e.target.value); setJsonError(null); }} />
          {jsonError && <div style={{ marginTop: '6px' }}><MessageBlock error>{jsonError}</MessageBlock></div>}
        </div>

        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button appearance="secondary" small onClick={load} disabled={loading || saving}>再読込</Button>
          <Button appearance="primary" onClick={onSave} disabled={saving || loading}>
            {saving ? <><InlineSpinner isLoading /> 保存中…</> : '保存 (POST settings)'}
          </Button>
        </div>
      </div>
    </CardBase>
  );
};

export default MonthEndJournalPage;
